import { Box, Container, styled } from '@mui/material';
import { useNavigate } from 'react-router';

import { WinnerQuotePhoto } from '@/competitions/_common/components/WinnerQuotePhoto.tsx';
import { LoanExampleText } from '@/components/LoanExampleText/index.ts';
import { competitionWinnersQuery } from '@/lib/api/requests.ts';
import { getQueryClient } from '@/lib/query-client-context.ts';
import { Timer } from '@/ui/Timer.tsx';

import ActionButton from '../../../../../components/ActionButton/index.ts';
import Page from '../../../../../components/Page/index.ts';
import { UnderlinedText } from '../../../../../components/UnderlinedText/index.ts';
import { useIsAfterDate } from '../../../../../hooks/useIsAfterDate.ts';
import { LANG } from '../../../../../services/Config.js';
import { MAY_2024_COMPETITION_END_DATE } from '../../config.ts';

import type { Route } from './+types/LandingPage';
import { Forest } from './_assets/Forest.tsx';
import { Hill } from './_assets/Hill.tsx';
import { House } from './_assets/House.tsx';
import madsHansen from './_assets/mads-hansen.webp';
import { Mountain } from './_assets/Mountain.tsx';
import { Things } from './_assets/Things.tsx';

const Root = styled('div')(({ theme }) => ({
  width: '100%',
  background: theme.palette.primary.light,
  textAlign: 'center',
  fontSize: '1rem',
  color: theme.palette.neutral.dark,
  lineHeight: '1.4',

  [theme.breakpoints.up('md')]: {
    fontSize: '1.125rem',
  },
}));

const CustomContainer = styled('div')(({ theme }) => ({
  margin: theme.spacing(4, 'auto'),
  gap: '16px',
  display: 'flex',
  flexDirection: 'column',
  width: '90%',

  [theme.breakpoints.up('sm')]: { width: '80%' },
  [theme.breakpoints.up('md')]: { width: '60%' },
  [theme.breakpoints.up('lg')]: { width: '55%' },
  [theme.breakpoints.up('xl')]: { width: '40%' },
}));

const Card = styled('div')(({ theme }) => ({
  '--card-padding': '20px',
  '--card-padding-bottom': 'calc(var(--card-padding) + 4px)',

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: 'var(--card-padding)',
  paddingBottom: 'var(--card-padding-bottom)',
  background: theme.palette.common.white,
  borderRadius: '8px',

  [theme.breakpoints.up('sm')]: {
    '--card-padding': '32px',
  },
}));

const Header = styled('div')(({ theme }) => ({
  color: theme.palette.text.default,
  fontWeight: 700,
  fontSize: '1.25rem',
  textWrap: 'balance',

  [theme.breakpoints.up('sm')]: {
    fontSize: '1.5rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1.625rem',
  },
}));

const Ul = styled('ul')(() => ({
  textAlign: 'left',
  margin: 0,
}));

const StyledActionButton = styled(ActionButton)(({ theme }) => ({
  '--color': '#FA8331',
  color: theme.palette.common.white,
  backgroundColor: 'var(--color)',
  borderColor: 'var(--color)',

  '&:hover': {
    borderColor: 'var(--color)',
    color: 'var(--color)',
  },

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    maxWidth: 300,
  },
}));

const OverflowingCard = styled(Card)(({ theme }) => ({
  '--overflow': '10px',
  width: `calc(100% + var(--overflow) * 2)`,
  marginLeft: 'calc(-1 * var(--overflow))',
  marginBottom: 'calc(var(--card-padding) * -1 - 8px)',
  isolation: 'isolate',
  border: `1px solid ${theme.palette.primary.main}`,
}));

const WinnerCard = styled('div')(({ theme }) => ({
  backgroundColor: '#F0F4FA',
  padding: theme.spacing(1, 2),
  color: theme.palette.text.default,
}));

export async function loader({ context }: Route.LoaderArgs) {
  const queryClient = getQueryClient(context);
  const competitions = await queryClient.query(competitionWinnersQuery());
  const competition = competitions.find((c) => c.slug === 'may_2024_campaign');
  return {
    winner: competition?.winners[0],
  };
}

export default function LandingPage({ loaderData }: Route.ComponentProps) {
  const navigate = useNavigate();
  const ended = useIsAfterDate(MAY_2024_COMPETITION_END_DATE);
  const winner = loaderData.winner;

  const CTA = ended ? (
    <StyledActionButton
      label="Søk om lavere rente"
      step="skip_start"
      flow="debt_check"
      color="secondary"
      gaEvent="may24_competition_cta_clicked"
    />
  ) : (
    <StyledActionButton
      label="Sjekk din gjeld og delta"
      step="init"
      flow="may_2024_campaign"
      color="secondary"
      gaEvent="may24_competition_cta_clicked"
    />
  );

  return (
    <Page title="Vårkampanje">
      <Root>
        <CustomContainer>
          <div>
            <Box
              sx={{
                isolation: 'isolate',
                position: 'relative',
                pt: 8,
                textAlign: 'left',
              }}
            >
              {/* Container for elements behind hill - all children must be absolutely positioned*/}
              <Box
                sx={{
                  position: 'relative',
                }}
              >
                <Mountain
                  sx={{
                    position: 'absolute',
                    height: 'auto',
                    width: { xs: 200, md: 270 },
                    left: { xs: -1, sm: 4, xl: 20 },
                    bottom: { xs: -46, md: -56, xl: -60 },
                  }}
                />

                <Forest
                  sx={{
                    position: 'absolute',
                    height: 'auto',
                    display: { xs: 'none', sm: 'block' },
                    right: { sm: 160, lg: 200 },
                    bottom: { sm: -45, lg: -52, xl: -60 },
                  }}
                />
              </Box>

              <Hill width="100%" height="100%" sx={{ mb: -1, position: 'relative' }} />

              <House
                sx={{
                  position: 'absolute',
                  height: 'auto',
                  width: { xs: 135, sm: 150, md: 175, lg: 200 },
                  left: { xs: 4, sm: 10, md: 20, lg: 30 },
                  bottom: { xs: 0, sm: 2, md: 4, lg: 8 },
                }}
              />

              <Things
                sx={{
                  position: 'absolute',
                  height: 'auto',
                  width: { xs: 120, sm: 150, md: 170 },
                  right: { xs: 4, sm: 8, md: 16, lg: 16 },
                  bottom: { xs: 0 },
                }}
              />
            </Box>

            <OverflowingCard>
              {ended ? (
                <>
                  <Header sx={{ mb: 2 }}>
                    Vinn inntil <UnderlinedText>300 000 kr</UnderlinedText> til nedbetaling av
                    forbruks- og kredittkortgjeld
                  </Header>
                  <p className="mb-2 typography-v1-body3">Denne trekningen er avsluttet</p>
                  {winner && (
                    <WinnerCard className="mb-6 typography-v1-h4">
                      Vinner: {winner.name} - {winner.age} - {winner.city}
                    </WinnerCard>
                  )}
                </>
              ) : (
                <>
                  <Header sx={{ mb: 1 }}>Sjekk om du kan gjøre en våropprydding</Header>

                  <Box
                    sx={{
                      mb: 4,
                    }}
                  >
                    Sjekk din gjeld og delta om å vinne inntil{' '}
                    <UnderlinedText variant="4">
                      <strong>300.000 kr</strong>
                    </UnderlinedText>{' '}
                    til nedbetaling av gjeld
                  </Box>
                </>
              )}

              {CTA}
            </OverflowingCard>

            <Card sx={{ color: (theme) => theme.palette.text.default }}>
              {ended ? (
                <p className="mt-8 typography-v1-body3">
                  I mai hadde vi en trekning hvor vi oppfordret folk til å sjekke om man kunne ta en
                  våropprydding i gjelden sin. Alle som sjekket gjelden sin deltok i trekningen og
                  premien var på hele 300 000 kroner til nedbetaling av usikret gjeld
                </p>
              ) : (
                <Box
                  sx={{
                    padding: '20px',
                    borderRadius: '8px',
                    backgroundColor: (theme) => theme.palette.background.default,
                  }}
                >
                  <strong>Tips til våropprydding av gjeld</strong>
                  <Ul sx={{ mt: 0.5 }}>
                    <li>Få oversikt over din gjeld</li>
                    <li>Sjekk om du kan få lavere rente</li>
                    <li>Luk ut de høye rentene ved å samle flere smålån til ett</li>
                  </Ul>
                </Box>
              )}
            </Card>
          </div>
        </CustomContainer>

        <Container>
          <WinnerQuotePhoto
            photo={madsHansen}
            photoAlt="Mads Hansen (34)"
            photoCaption="Foto: Knut Berg Jenssen / Dagbladet"
            quote="- Hehe, ja. Da jeg skjønte at jeg hadde blitt trukket ut i maitrekningen og at Scoopr faktisk kom til å betale ned hele forbruksgjelden min, da stokket ordene seg for meg. Det var som vi sier her i Tromsø - jævlig artig!"
            quoteCaption="Sier Mads Hansen (34), vinner av Mai-trekningen, til Dagbladet"
            link="https://www.dagbladet.no/annonse/mads-hansen-34-fra-tromso-fikk-slettet-hele-sin-forbruksgjeld/81523709"
          />
        </Container>

        <CustomContainer sx={{ mb: 16 }}>
          <Card>
            <Timer
              date={MAY_2024_COMPETITION_END_DATE}
              theme={{
                backgroundColor: '#18A0FB',
                digitsColor: '#FFFFFF',
              }}
            />

            <Box sx={{ textWrap: 'balance', my: 4, fontSize: '1.125em' }}>
              3. juni kl. 12:00 trekker vi en heldig vinner som får nedbetalt{' '}
              <strong>inntil 300.000 kr</strong> av sin gjeld.
            </Box>

            {CTA}
          </Card>

          <Card sx={{ textAlign: 'left' }}>
            Vi mener at alle bør sjekke om de kan få lavere rente på sine forbrukslån og
            kredittkort.
            <br />
            <br />
            I 2023 hjalp vi ti-tusenvis av nordmenn med å få lavere rente, og sammen klarte vi å
            kutte rentekostnadene med 250 millioner kroner!
            <br />
            <br />
            Vi vet at enda flere kan få lavere rente. Derfor ønsker vi nå å rette fokus mot
            vårrengjøring slik at enda flere blir oppmerksom på å senke renten der de kan.
            <br />
            <br />
            <strong>Det er vel ikke vits å betale mer enn man må?</strong>
          </Card>

          <Card>
            <Header sx={{ mb: { xs: 2, md: 2.5 } }}>
              Hvilken type gjeld nedbetales dersom jeg er heldig og vinner?
            </Header>
            <Ul>
              <li>Forbrukslån</li>
              <li>Rentebærende kredittkortgjeld</li>
            </Ul>
          </Card>

          <Card>
            <Header sx={{ mb: { xs: 2, md: 2.5 } }}>
              Hvorfor lønner det seg å refinansiere gjennom Scoopr?
            </Header>

            <Box sx={{ textAlign: 'left' }}>
              Refinansiering er kort fortalt å{' '}
              <strong>samle flere lån til ett nytt lån med lavere rente</strong>
              .
              <br />
              <br />
              Scoopr samarbeider med flere kjente norske banker for å gjøre det enklere for deg å
              finne en lavere rente.
              <br />
              <br />
              Med lavere rente kan du <strong>
                betale mindre i måneden, senke totalkostnaden
              </strong>{' '}
              på lånet og <strong>kutte unødvendige kostnader</strong>.
            </Box>
          </Card>

          <Card>
            <Header sx={{ fontStyle: 'italic' }}>
              Vi mener at det er viktig å sjekke om du kan få lavere rente. Det kan nemlig spille en
              stor rolle i din økonomi.
            </Header>
          </Card>

          <Card>
            <Header sx={{ mb: { xs: 2, md: 2.5 } }}>På Scoopr får du oversikt over</Header>
            <Ul sx={{ mb: 2 }}>
              <li>
                Din <strong>gjeld</strong>
              </li>
              <li>
                Din <strong>kredittscore</strong>
              </li>
              <li>
                Dine <strong>betalingsanmerkninger</strong>
              </li>
              <li>
                og et <strong>gratis</strong> estimat av din <strong>boligverdi</strong>.
              </li>
            </Ul>

            {/* @ts-ignore */}
            <StyledActionButton
              label="Min Scoopr"
              color="secondary"
              onClick={() => {
                void navigate(LANG.routes.MY_SCOOPR_LOGIN);
              }}
            />
          </Card>

          <div className="text-[0.875em]">
            <LoanExampleText />
          </div>
        </CustomContainer>
      </Root>
    </Page>
  );
}
